import { useSuspenseQuery } from '@tanstack/react-query';
import {
  prefetchMemberScope,
  transformFilterKeysToQueryParams,
  WidgetApiV2,
} from '../../../../shared/service/widgetApi';
import {
  WidgetDataResponse,
  WidgetKind,
} from '../../../../shared/types/widget';
import useFilters from '../../../../utilities/hooks/useFilters';
import { useFilterData } from '../../../../utilities/hooks/useFilterData';

/**
 * useWidgetDataQuery - Suspense-enabled data fetching for widgets
 *
 * - Component suspends while data is loading (no manual loading states)
 * - Data is guaranteed to exist when the component renders
 * - Automatic caching, deduplication, and background refetching via React Query
 *
 * IMPORTANT: useSuspenseQuery does NOT support an `enabled` parameter.
 * To conditionally fetch, control when the calling component (WidgetContent) mounts.
 * See WidgetRenderer for the viewport-based mounting logic.
 *
 * @param widgetId - The unique identifier for the widget to fetch data for
 * @returns Query result with guaranteed data (suspends until available)
 */

export default function useWidgetDataQuery<T extends WidgetKind>(
  widgetId: string,
) {
  const { getFilters } = useFilters();
  const { configData } = useFilterData();

  const appliedFilters = getFilters();
  const hasSelectedOrganisation = Array.from(
    appliedFilters.selectedOrganisations.values(),
  ).some(Boolean);
  const requiresOrganisationSelection =
    !!configData?.showOrganisationDropdown && !hasSelectedOrganisation;

  const filterParams = transformFilterKeysToQueryParams(appliedFilters);

  return useSuspenseQuery<WidgetDataResponse<T> | null>({
    queryKey: [
      'widget',
      'data',
      widgetId,
      filterParams,
      requiresOrganisationSelection,
    ],
    queryFn: async () => {
      if (requiresOrganisationSelection) {
        return null;
      }

      await prefetchMemberScope(appliedFilters);
      return WidgetApiV2(widgetId, appliedFilters);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    // retry with exponential backoff
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}
